import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  usePiece,
  usePieceArrangements,
  useAddToRepertoire,
} from '@/lib/queries/pieces';
import { useUserArrangementsForPiece } from '@/lib/queries/user-arrangements-by-piece';
import {
  useMasteryLevels,
  useQueueStatuses,
  useKeys,
  useTimeSignatures,
} from '@/lib/queries/reference-data';
import { isUserArrangementExistsError, type UserArrangementExistsError } from '@/lib/queries/repertoire-errors';
import { ApiError } from '@/lib/api-client';
import { AddRepertoireHeaderBand } from '@/components/molecules/add-repertoire-header-band';
import { ArrangementSubPicker } from '@/components/molecules/arrangement-sub-picker';
import { DiscardChangesDialog } from '@/components/molecules/discard-changes-dialog';
import {
  RepertoireEntryForm,
  type RepertoireEntryFormData,
} from '@/components/organisms/repertoire-entry-form';
import { Button } from '@/components/atoms/button';
import { pickDefaultArrangement } from '@/lib/pick-default-arrangement';
import type { Arrangement } from '@/lib/types';

const FORM_ID = 'add-repertoire-embedded-form';

const COPY = {
  noArrangements: 'This piece has no arrangements yet — open it in the library to add one.',
  arrangementsErrorHeading: "Couldn't load this piece",
  arrangementsErrorBody: 'The piece may have been removed. Refresh search to pick another.',
  refreshSearch: 'Refresh search',
  blockedAllAddedHeading: (title: string) => `You already have every setting of "${title}" in your repertoire.`,
  blockedAllAddedBody: 'Pick a different piece, or open one of your existing entries.',
  pickDifferent: 'Pick a different piece',
  partialOverlap: (added: number, total: number, title: string) =>
    `You already have ${added} of ${total} settings of "${title}" in your repertoire — pick a different setting to add another.`,
  saveFailedHeading: "Couldn't save",
  saveFailedBody: 'Please try again. If the problem persists, refresh the page.',
  notFoundHeading: 'This arrangement is no longer available.',
  notFoundBody: 'It may have been removed since you opened the picker. Pick a different piece.',
  openExistingEntry: 'Open existing entry',
  cancel: 'Cancel',
  add: 'Add to repertoire',
} as const;

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'duplicate'; existingUserArrangementId: string; detail: string }
  | { kind: 'serverError' }
  | { kind: 'notFound' };

type ResolvedView =
  | { kind: 'loading' }
  | { kind: 'arrangementsError404' }
  | { kind: 'noArrangements' }
  | { kind: 'allInRepertoire'; arrangements: readonly Arrangement[]; userArrangementByArrangementId: ReadonlyMap<string, string> }
  | { kind: 'form'; arrangements: readonly Arrangement[]; userArrangementByArrangementId: ReadonlyMap<string, string> };

function resolveView(args: {
  arrangementsLoading: boolean;
  arrangementsError: unknown;
  arrangements: readonly Arrangement[] | undefined;
  userArrangementByArrangementId: ReadonlyMap<string, string>;
}): ResolvedView {
  if (args.arrangementsLoading) return { kind: 'loading' };
  if (args.arrangementsError instanceof ApiError && args.arrangementsError.status === 404) {
    return { kind: 'arrangementsError404' };
  }
  const arrangements = args.arrangements ?? [];
  if (arrangements.length === 0) return { kind: 'noArrangements' };
  const allAdded = arrangements.every((a) =>
    args.userArrangementByArrangementId.has(a.arrangementId),
  );
  if (allAdded) {
    return {
      kind: 'allInRepertoire',
      arrangements,
      userArrangementByArrangementId: args.userArrangementByArrangementId,
    };
  }
  return {
    kind: 'form',
    arrangements,
    userArrangementByArrangementId: args.userArrangementByArrangementId,
  };
}

export type AddRepertoireFormPaneProps = {
  pieceId: string;
  /**
   * Notifies the parent (the `/repertoire/add` route) whenever the embedded
   * form transitions between clean and dirty. The route uses it to gate
   * piece-switch navigation behind the discard-changes dialog. Cancel-while-
   * dirty is handled internally by this component.
   */
  onDirtyChange?: (isDirty: boolean) => void;
};

export function AddRepertoireFormPane({ pieceId, onDirtyChange }: AddRepertoireFormPaneProps): ReactElement {
  const navigate = useNavigate();
  const pieceQ = usePiece(pieceId);
  const arrangementsQ = usePieceArrangements(pieceId);
  const userArrangements = useUserArrangementsForPiece(pieceId);
  const masteryLevelsQ = useMasteryLevels();
  const queueStatusesQ = useQueueStatuses();
  const keysQ = useKeys();
  const timeSignaturesQ = useTimeSignatures();
  const addM = useAddToRepertoire();

  const arrangements: readonly Arrangement[] | undefined = arrangementsQ.data?.content;
  const view = useMemo(
    () =>
      resolveView({
        arrangementsLoading: Boolean(arrangementsQ.isLoading),
        arrangementsError: arrangementsQ.error,
        arrangements,
        userArrangementByArrangementId: userArrangements.data,
      }),
    [arrangementsQ.isLoading, arrangementsQ.error, arrangements, userArrangements.data],
  );

  const defaultArrangementId = useMemo(() => {
    if (view.kind !== 'form') return null;
    const userIds = new Set(view.userArrangementByArrangementId.keys());
    return pickDefaultArrangement(view.arrangements, userIds)?.arrangementId ?? null;
  }, [view]);

  const [selectedArrangementId, setSelectedArrangementId] = useState<string | null>(defaultArrangementId);
  const userTouchedRef = useRef<boolean>(false);
  // Re-seed when the resolved default changes (e.g. arrangements or
  // user-arrangements arrive late and reveal the current selection is no
  // longer addable). Suppressed once the user has explicitly picked.
  useEffect(() => {
    if (userTouchedRef.current) return;
    if (defaultArrangementId === null) return;
    if (defaultArrangementId === selectedArrangementId) return;
    setSelectedArrangementId(defaultArrangementId);
  }, [defaultArrangementId, selectedArrangementId]);

  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);

  const handleDirtyChange = useCallback(
    (next: boolean): void => {
      setIsDirty(next);
      onDirtyChange?.(next);
    },
    [onDirtyChange],
  );

  const handleSelectArrangement = (arrangementId: string): void => {
    userTouchedRef.current = true;
    setSelectedArrangementId(arrangementId);
    setSubmitState({ kind: 'idle' });
  };

  const handleSubmit = async (data: RepertoireEntryFormData): Promise<void> => {
    if (selectedArrangementId === null) return;
    setSubmitState({ kind: 'submitting' });
    try {
      const entry = await addM.mutateAsync({
        arrangementId: selectedArrangementId,
        queueStatusId: data.queueStatusId,
        masteryLevelId: data.masteryLevelId ?? undefined,
        keyId: data.keyId ?? undefined,
        practiceTempoBeats: data.practiceTempoBeats ?? undefined,
        performanceTempoBeats: data.performanceTempoBeats ?? undefined,
        notes: data.notes,
        firstPracticeOn: data.firstPracticeOn ?? null,
      });
      void navigate({
        to: '/repertoire/$arrangementId',
        params: { arrangementId: entry.arrangementId },
      });
    } catch (error: unknown) {
      if (isUserArrangementExistsError(error)) {
        const body = error.body as UserArrangementExistsError;
        setSubmitState({
          kind: 'duplicate',
          existingUserArrangementId: body.user_arrangement_id,
          detail: body.detail,
        });
        return;
      }
      if (error instanceof ApiError && error.status === 404) {
        setSubmitState({ kind: 'notFound' });
        return;
      }
      setSubmitState({ kind: 'serverError' });
    }
  };

  const navigateAwayFromAdd = (): void => {
    void navigate({ to: '/library' });
  };

  const handleCancel = (): void => {
    if (isDirty) {
      setIsCancelDialogOpen(true);
      return;
    }
    navigateAwayFromAdd();
  };

  const handleConfirmDiscard = (): void => {
    setIsCancelDialogOpen(false);
    navigateAwayFromAdd();
  };

  const handleKeepEditing = (): void => {
    setIsCancelDialogOpen(false);
  };

  const handlePickDifferentPiece = (): void => {
    void navigate({ to: '/repertoire/add' });
  };

  const handleRefreshSearch = (): void => {
    void navigate({ to: '/repertoire/add' });
  };

  const piece = pieceQ.data;
  const pieceTitle = piece?.prettyName ?? 'this piece';

  if (view.kind === 'loading') {
    return (
      <section className="flex min-h-screen items-center justify-center px-10 text-sm text-[var(--ink-3)]">
        Loading the selected piece…
      </section>
    );
  }

  if (view.kind === 'arrangementsError404') {
    return (
      <section className="flex min-h-screen flex-col items-center justify-center gap-4 px-10 text-center">
        <h2 className="font-display text-xl font-semibold text-[var(--ink)]">
          {COPY.arrangementsErrorHeading}
        </h2>
        <p className="max-w-md text-sm text-[var(--ink-3)]">{COPY.arrangementsErrorBody}</p>
        <Button variant="secondary" size="sm" onClick={handleRefreshSearch}>
          {COPY.refreshSearch}
        </Button>
      </section>
    );
  }

  if (view.kind === 'noArrangements') {
    return (
      <section className="flex min-h-screen flex-col items-center justify-center gap-4 px-10 text-center">
        <h2 className="font-display text-xl font-semibold text-[var(--ink)]">
          {pieceTitle} has no arrangements
        </h2>
        <p className="max-w-md text-sm text-[var(--ink-3)]">{COPY.noArrangements}</p>
        <Link
          to="/library/$pieceId"
          params={{ pieceId }}
          className="inline-flex h-10 items-center gap-2 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] px-3 text-xs font-bold text-[var(--ink)] transition-colors hover:border-[var(--brand)]"
        >
          Open in library
        </Link>
      </section>
    );
  }

  if (view.kind === 'allInRepertoire') {
    return (
      <section className="flex min-h-screen flex-col items-center justify-center gap-4 px-10 py-10 text-center">
        <h2 className="font-display text-xl font-semibold text-[var(--ink)]">
          {COPY.blockedAllAddedHeading(pieceTitle)}
        </h2>
        <p className="max-w-md text-sm text-[var(--ink-3)]">{COPY.blockedAllAddedBody}</p>
        <ul className="flex w-full max-w-md flex-col gap-2">
          {view.arrangements.map((arrangement) => {
            const userArrangementId = view.userArrangementByArrangementId.get(arrangement.arrangementId);
            if (userArrangementId === undefined) return null;
            return (
              <li key={arrangement.arrangementId}>
                <Link
                  to="/repertoire/$arrangementId"
                  params={{ arrangementId: userArrangementId }}
                  className="flex items-center justify-between rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-4 py-3 text-sm font-medium text-[var(--ink)] transition-colors hover:border-[var(--brand)]"
                >
                  <span>{arrangement.prettyName}</span>
                  <span className="text-xs text-[var(--ink-3)]">Open</span>
                </Link>
              </li>
            );
          })}
        </ul>
        <Button variant="primary" size="sm" onClick={handlePickDifferentPiece}>
          {COPY.pickDifferent}
        </Button>
      </section>
    );
  }

  // view.kind === 'form'
  if (submitState.kind === 'notFound') {
    return (
      <section className="flex min-h-screen flex-col items-center justify-center gap-4 px-10 text-center">
        <h2 className="font-display text-xl font-semibold text-[var(--ink)]">
          {COPY.notFoundHeading}
        </h2>
        <p className="max-w-md text-sm text-[var(--ink-3)]">{COPY.notFoundBody}</p>
        <Button variant="primary" size="sm" onClick={handlePickDifferentPiece}>
          {COPY.pickDifferent}
        </Button>
      </section>
    );
  }

  const totalArrangements = view.arrangements.length;
  const addedCount = view.userArrangementByArrangementId.size;
  const showPartialOverlap = addedCount > 0 && addedCount < totalArrangements;

  const masteryLevels = masteryLevelsQ.data?.content ?? [];
  const queueStatuses = queueStatusesQ.data?.content ?? [];
  const keys = keysQ.data?.content ?? [];
  const timeSignatures = timeSignaturesQ.data?.content ?? [];

  const referenceDataLoaded = masteryLevels.length > 0 && queueStatuses.length > 0;
  const isSubmitting = submitState.kind === 'submitting' || addM.isPending;
  const submitDisabled =
    isSubmitting ||
    selectedArrangementId === null ||
    !referenceDataLoaded ||
    submitState.kind === 'duplicate';

  const formArrangements: Arrangement[] = view.arrangements as Arrangement[];

  return (
    <section className="flex min-h-screen flex-col">
      <div className="flex flex-1 flex-col gap-8 px-10 py-10">
        <AddRepertoireHeaderBand
          pieceId={pieceId}
          pieceTitle={pieceTitle}
          composerName={piece?.composerName ?? null}
          formNames={collectFormNames(view.arrangements)}
        />

        {showPartialOverlap && (
          <p className="rounded-[var(--r-sm)] border border-[var(--warm)] bg-[var(--warm-wash)] p-3 text-sm text-[var(--warm)]">
            {COPY.partialOverlap(addedCount, totalArrangements, pieceTitle)}
          </p>
        )}

        <ArrangementSubPicker
          arrangements={view.arrangements}
          selectedArrangementId={selectedArrangementId ?? ''}
          userArrangementByArrangementId={view.userArrangementByArrangementId}
          onSelect={handleSelectArrangement}
        />

        {selectedArrangementId !== null && referenceDataLoaded && (
          <RepertoireEntryForm
            key={selectedArrangementId}
            mode="create"
            variant="embedded"
            htmlFormId={FORM_ID}
            arrangements={formArrangements}
            selectedArrangementId={selectedArrangementId}
            masteryLevels={masteryLevels}
            queueStatuses={queueStatuses}
            keys={keys}
            timeSignatures={timeSignatures}
            onSave={(data) => {
              void handleSubmit(data);
            }}
            onCancel={handleCancel}
            isSaving={isSubmitting}
            showDangerZone={false}
            showSharedMetadata={false}
            onDirtyChange={handleDirtyChange}
          />
        )}

        {submitState.kind === 'duplicate' && (
          <div
            role="alert"
            className="rounded-[var(--r-sm)] border border-[var(--error,red)] bg-[var(--error-wash,#fdecec)] p-4 text-sm text-[var(--error,red)]"
          >
            <p className="font-bold">{submitState.detail}</p>
            <Link
              to="/repertoire/$arrangementId"
              params={{ arrangementId: submitState.existingUserArrangementId }}
              className="mt-2 inline-block text-sm font-bold underline"
            >
              {COPY.openExistingEntry}
            </Link>
          </div>
        )}

        {submitState.kind === 'serverError' && (
          <div
            role="alert"
            className="rounded-[var(--r-sm)] border border-[var(--error,red)] bg-[var(--error-wash,#fdecec)] p-4 text-sm text-[var(--error,red)]"
          >
            <p className="font-bold">{COPY.saveFailedHeading}</p>
            <p className="mt-1">{COPY.saveFailedBody}</p>
          </div>
        )}
      </div>

      <footer className="sticky bottom-0 flex items-center justify-between gap-4 border-t border-[var(--rule)] bg-[var(--bg-surface)] px-10 py-4">
        <p className="truncate text-sm text-[var(--ink-3)]">
          Add {pieceTitle}
          {selectedArrangementId !== null && (
            <>
              {' · '}
              {view.arrangements.find((a) => a.arrangementId === selectedArrangementId)?.prettyName}
            </>
          )}
        </p>
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            {COPY.cancel}
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            form={FORM_ID}
            disabled={submitDisabled}
            aria-busy={isSubmitting || undefined}
          >
            {COPY.add}
          </Button>
        </div>
      </footer>

      {isCancelDialogOpen && (
        <DiscardChangesDialog
          onConfirm={handleConfirmDiscard}
          onCancel={handleKeepEditing}
        />
      )}
    </section>
  );
}

function collectFormNames(arrangements: readonly Arrangement[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const arrangement of arrangements) {
    const formName = arrangement.formName?.trim();
    if (formName && !seen.has(formName)) {
      seen.add(formName);
      out.push(formName);
    }
  }
  return out;
}
